import type { OverlaySlotHandle } from '@opengov/psp-contract';

/** Snapshot consumed by `OverlayDrawerHost` via `useSyncExternalStore`. */
export interface OverlayHostSnapshot {
  readonly open: boolean;
  readonly ariaLabel: string;
  /** Active claim's portal target when a claim exists; null when none. */
  readonly element: HTMLDivElement | null;
  /** Increments on every successful `setOpen(true)` (including preemption). */
  readonly openSeq: number;
  /** Increments whenever the overlay transitions to fully closed. */
  readonly closeSeq: number;
}

interface ClaimOptions {
  ariaLabel: string;
  /** Pre-made element so `useOverlaySlot` can expose `portalTargetRef` before
   *  the mount effect runs (mirrors `useDrawerSlot`). */
  element?: HTMLDivElement;
  onRequestClose?: () => void;
}

interface ClaimRecord {
  ariaLabel: string;
  element: HTMLDivElement;
  onRequestClose?: () => void;
  open: boolean;
}

const EMPTY_SNAPSHOT: OverlayHostSnapshot = {
  open: false,
  ariaLabel: '',
  element: null,
  openSeq: 0,
  closeSeq: 0,
};

/**
 * Singleton claim store for the shell's hovering overlay sidebar. Multiple
 * callers may claim (shell Notifications, Resource Center, remotes); only one
 * claim can be open at a time — a later `setOpen(true)` preempts the prior
 * occupant. Lives as a module singleton so the imperative shell-bridge API can
 * claim from outside the shell React tree.
 */
export class OverlaySlotStore {
  private claims = new Map<string, ClaimRecord>();
  private activeId: string | null = null;
  private open = false;
  private openSeq = 0;
  private closeSeq = 0;
  private seq = 0;
  private listeners = new Set<() => void>();
  private snapshot: OverlayHostSnapshot = EMPTY_SNAPSHOT;

  claim({ ariaLabel, element, onRequestClose }: ClaimOptions): OverlaySlotHandle {
    const id = `overlay-claim-${this.seq}`;
    this.seq += 1;
    const node = element ?? document.createElement('div');
    this.claims.set(id, { ariaLabel, element: node, onRequestClose, open: false });

    return {
      element: node,
      setOpen: (next: boolean) => this.setOpen(id, next),
      release: () => this.release(id),
    };
  }

  setOpen(id: string, open: boolean): void {
    const claim = this.claims.get(id);
    if (!claim) return;

    if (open) {
      // Already the active open claim — no re-emit.
      if (this.activeId === id && this.open) return;

      if (this.activeId && this.activeId !== id && this.open) {
        this.invokeCloseHook(this.activeId);
        const prev = this.claims.get(this.activeId);
        if (prev) prev.open = false;
      }

      claim.open = true;
      this.activeId = id;
      this.open = true;
      this.openSeq += 1;
      this.rebuildSnapshot();
      this.emit();
      return;
    }

    claim.open = false;
    // Closing a non-active claim does not affect the host.
    if (this.activeId !== id || !this.open) return;

    this.open = false;
    this.closeSeq += 1;
    this.rebuildSnapshot();
    this.emit();
  }

  /**
   * Close the active overlay if open. Invokes the active claim's
   * `onRequestClose` so the owner can sync local UI state.
   */
  close(): void {
    if (!this.open || !this.activeId) return;
    this.invokeCloseHook(this.activeId);
    const claim = this.claims.get(this.activeId);
    if (claim) claim.open = false;
    this.open = false;
    this.closeSeq += 1;
    this.rebuildSnapshot();
    this.emit();
  }

  release(id: string): void {
    if (!this.claims.has(id)) return;
    const wasActiveOpen = this.activeId === id && this.open;
    this.claims.delete(id);

    if (wasActiveOpen) {
      this.open = false;
      this.activeId = null;
      this.closeSeq += 1;
      this.rebuildSnapshot();
      this.emit();
      return;
    }

    if (this.activeId === id) {
      // Keep-mounted closed state was showing this claim's element — clear it.
      this.activeId = null;
      this.rebuildSnapshot();
      this.emit();
    }
  }

  /** Drop every claim. Used by tests that share the app-wide singleton. */
  clearAll(): void {
    const wasOpen = this.open;
    this.claims.clear();
    this.activeId = null;
    this.open = false;
    if (wasOpen) this.closeSeq += 1;
    this.rebuildSnapshot();
    this.emit();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): OverlayHostSnapshot => this.snapshot;

  private invokeCloseHook(id: string): void {
    try {
      this.claims.get(id)?.onRequestClose?.();
    } catch (err) {
      console.error(`[OverlaySlots] onRequestClose threw for "${id}":`, err);
    }
  }

  private rebuildSnapshot(): void {
    const active = this.activeId ? this.claims.get(this.activeId) : undefined;
    this.snapshot = {
      open: this.open,
      ariaLabel: active?.ariaLabel ?? '',
      element: active?.element ?? null,
      openSeq: this.openSeq,
      closeSeq: this.closeSeq,
    };
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

/** App-wide singleton. Tests construct their own `new OverlaySlotStore()`. */
export const overlaySlotStore = new OverlaySlotStore();
